// 警報處理狀態（EVT-06）的共用快取：每個看得到的家戶撈最近的警報與未處理數（每分鐘），
// 串流的 alarm_state 幀與自己剛按的結果蓋在上面（另一個人按〔我來處理〕，這邊 3 秒內就變「A 處理中」，W25）。
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useLiveEvents } from './liveEvents';
import { useScope } from './scope';
import { alarmApi, infoFromFrame, isUnhandled, type AlarmInfo, type AlarmItem, type AlarmSla } from './alarms';
import { withAlarmPhases } from './alarmPhases';
import { pairEpisodes, phaseOf, type Episode } from './eventEpisodes';
import { compareAlarm } from './alarmGroup';
import { listedSet, tidyCount } from './alarmTidy';

interface Ctx {
  /** 這筆警報目前的狀態；不知道（不在最近的清單、也沒收到變更）→ undefined */
  infoOf(eventId: string): AlarmInfo | undefined;
  /** 這個家戶能不能處理（guest 只能看）；還沒讀到 → false */
  canHandle(homeId: string): boolean;
  /** 家戶 → plane api */
  apiOf(homeId: string): string | undefined;
  /**
   * 未處理的警報**次數**（open + acknowledged；同一次的開始＋結束算一次，第九輪 B9-01，跟 facet 同一個單位）。
   * 平台給事件次數（每筆帶 counted，第九輪平台收尾）時直接用平台的 unhandled，清單一到就有（B10-01）；
   * 舊平台要等每個家戶「還沒處理」的完整清單都讀到才有。還沒有 → null（畫面寫「計算中」，不先寫紀錄筆數、不整行拿掉）。
   */
  unhandled: number | null;
  /** 有家戶的未處理紀錄超過 OPEN_MAX、沒讀完：數字寫「N+」 */
  unhandledMore: boolean;
  /**
   * 這個 device_ref 在「我的設備」裡嗎（SPEC #vms-web-alarm-tidy）：未登記的通道、已移除的設備 → false，它們的警報不算進計數。
   * 「我的設備」還在讀、或那個 plane 讀不到 → 當成在（不因為讀不到就把警報藏掉）。
   */
  listed(homeId: string, deviceRef: string): boolean;
  /** 還沒處理、但來自未登記 / 已移除設備的次數（總覽「另有 N 則…不算在內」）；還沒算好 → 0 */
  unlistedOpen: number;
  /** 平台說有沒有未處理的警報（事件頁預設分頁用；不等完整清單）；還沒讀到 → null */
  hasUnhandled: boolean | null;
  /**
   * 還沒處理的警報，一次一列（同一次的開始＋結束合成一列，列在開始那筆；ep = 那一次、ids = 這一次的每一筆紀錄），新的在前；
   * 總覽「還沒處理的警報」列最新 5 則（第二輪 R2-P1-1、B2-08）
   */
  openList: OpenAlarm[];
  /**
   * 同一台＋同一種還沒處理的總數與最早時間（第六輪 B6-03：總覽、警報分頁、全部事件都寫同一個「× N 次（最早 HH:MM）」）。
   * 來源是每個家戶「還沒處理」的完整清單（最多 OPEN_MAX 則），不是畫面這一頁載入的；沒有 → undefined。
   */
  openAgg(homeId: string, deviceRef: string, key: string): OpenAgg | undefined;
  /**
   * 目前範圍每個家戶「還沒處理」的完整清單都讀到了（或讀不到、只能用最近 200 則）。還沒到時合併次數寫「計算中」，
   * 不先寫最近 200 則算出來的次數（第八輪 B8-03：切範圍後先寫 × 134，facet 是 512）。
   */
  openReady: boolean;
  /** 接手時限（EVT-07）：清單每筆帶的 sla（不在最近 / 還沒處理的清單裡 → undefined） */
  slaOf(eventId: string): AlarmSla | undefined;
  /** 自己剛改完 / 409 拿到的目前狀態 */
  set(homeId: string, eventId: string, info: AlarmInfo): void;
  /**
   * 範圍外的家戶也要能處理（警報浮窗切範圍後不掉按鈕，VMS UX 評審第三輪 B3-03）：登記後一併撈那個家戶的警報（不算進未處理數）。
   * 回傳取消登記。
   */
  watch(homeId: string): () => void;
}

const AlarmCtx = createContext<Ctx>({
  infoOf: () => undefined,
  canHandle: () => false,
  apiOf: () => undefined,
  unhandled: null,
  unhandledMore: false,
  listed: () => true,
  unlistedOpen: 0,
  hasUnhandled: null,
  openList: [],
  openAgg: () => undefined,
  openReady: true,
  slaOf: () => undefined,
  set: () => {},
  watch: () => () => {},
});

const OVERLAY_MAX = 500;
/** 每個家戶最多讀多少則還沒處理的警報（200 一頁）；超過的不算進合併次數 */
export const OPEN_MAX = 1000;
const OPEN_PAGE = 200;

/** 還沒處理的一次警報（開始＋結束合成一列）：列在開始那筆（沒有開始就是結束那筆）。 */
export type OpenAlarm = AlarmItem & { ep?: Episode; ids: string[] };

export interface OpenAgg {
  /** 次數（同一次的開始＋結束算一次） */
  n: number;
  /** 最早一則（ms） */
  earliest: number;
  /** 這一波每一筆紀錄的 event_id（新到舊，開始與結束都在）：合併列的〔處理 ▾〕一次套用 */
  ids: string[];
}

/** 合併鍵：同一台＋同一種。 */
export const aggKey = (homeId: string, deviceRef: string, key: string) => `${homeId}/${deviceRef}/${key}`;

/**
 * 還沒處理的紀錄（新到舊）→ 一次一列（第九輪 B9-01）：同一次的開始＋結束（同 device_ref＋binding_id＋key，清單合列的同一個配對函式）
 * 合成一列、列在開始那筆；沒有 phase 的一筆一列。回傳順序新到舊（依那一列的時間）。
 */
export function openEpisodes(list: AlarmItem[]): OpenAlarm[] {
  const eps = pairEpisodes(
    list.flatMap((a) => {
      const ph = phaseOf(a);
      return ph ? [{ id: a.event_id, ts: Date.parse(a.occurred_at) || 0, phase: ph, ref: `${a.home_id}/${a.device_ref}`, binding: a.binding_id || undefined, key: a.key }] : [];
    }),
  );
  const out: OpenAlarm[] = [];
  const byEp = new Map<Episode, AlarmItem[]>();
  for (const a of list) {
    const ep = eps.get(a.event_id);
    if (!ep) {
      // 平台說這筆不算一次（counted = false）又配不到同一次的其他筆：那一次算在已處理的那筆（例：開始已處理、之後才進來的結束），不另算一次
      if (a.counted !== false) out.push({ ...a, ids: [a.event_id] });
      continue;
    }
    const l = byEp.get(ep);
    if (l) l.push(a);
    else byEp.set(ep, [a]);
  }
  for (const [ep, recs] of byEp) {
    // 一次事件的處理狀態看 counted 那筆（SPEC #vms-web-290 第九輪平台收尾）：這一次在未處理清單裡的每一筆都是 counted = false
    // → 算次數的那筆已處理（開始處理完之後才進來的結束），不算還沒處理的一次。舊平台沒有 counted（undefined）照算。
    if (recs.every((a) => a.counted === false)) continue;
    // 列在開始那筆（時間 = 開始時間）；開始不在未處理清單裡（已處理或在更早）就列結束那筆；同一次的每一筆（含重複上報）都一起處理
    const headId = (ep.start ?? ep.end)?.id;
    const head = recs.find((a) => a.event_id === headId) ?? recs[recs.length - 1];
    out.push({ ...head, ep, ids: recs.map((a) => a.event_id) });
  }
  // 跟警報分頁同一個排序（同一秒依設備鍵、key、event_id；第十一輪 B11-05）
  const k = (a: OpenAlarm) => ({ ts: Date.parse(a.occurred_at) || 0, ref: a.device_ref, key: a.key, id: a.event_id });
  return out.sort((a, b) => compareAlarm(k(a), k(b)));
}

/** 還沒處理的（一次一列，新到舊）→ 每個合併鍵的次數、最早時間與每一筆紀錄的 id。 */
export function aggregateOpen(list: (Pick<OpenAlarm, 'event_id' | 'home_id' | 'device_ref' | 'key' | 'occurred_at'> & { ids?: string[] })[]): Map<string, OpenAgg> {
  const m = new Map<string, OpenAgg>();
  for (const a of list) {
    const k = aggKey(a.home_id, a.device_ref, a.key);
    const ts = Date.parse(a.occurred_at) || 0;
    const ids = a.ids?.length ? a.ids : [a.event_id];
    const g = m.get(k);
    if (g) {
      g.n += 1;
      g.earliest = Math.min(g.earliest, ts);
      g.ids.push(...ids);
    } else m.set(k, { n: 1, earliest: ts, ids: [...ids] });
  }
  return m;
}

/**
 * 一個家戶全部還沒處理的警報（open + acknowledged），翻頁到 OPEN_MAX。平台每筆帶 phase / binding_id / counted（第九輪平台收尾）時直接用；
 * 舊平台（沒有 counted）才用全文檢索補 phase / binding_id（第九輪 B9-01；B10-01：補查要等 40 多秒）。
 */
export async function listOpen(api: string, homeId: string, list = alarmApi.list, addPhases = withAlarmPhases): Promise<{ items: AlarmItem[]; more: boolean }> {
  const out: AlarmItem[] = [];
  let cursor: string | undefined;
  let eventCounts = true;
  do {
    const r = await list(api, homeId, { state: ['open', 'acknowledged'], limit: OPEN_PAGE, ...(cursor ? { cursor } : {}) });
    out.push(...r.alarms);
    eventCounts = eventCounts && r.eventCounts;
    cursor = r.next_cursor || undefined;
  } while (cursor && out.length < OPEN_MAX);
  return { items: eventCounts ? out : await addPhases(api, homeId, out), more: !!cursor };
}

/** 兩個來源取較新的（串流斷過線時，清單重撈到的可能比蓋上去的新）。 */
export function newer(a: AlarmInfo | undefined, b: AlarmInfo | undefined): AlarmInfo | undefined {
  if (!a || !b) return a ?? b;
  // 比版本（每次變更 +1；同一毫秒內兩次變更、串流重送舊幀都分得出，plan#36）；同版本才比時間
  const va = a.version ?? -1;
  const vb = b.version ?? -1;
  if (va !== vb) return vb > va ? b : a;
  return (Date.parse(b.updated_at ?? '') || 0) > (Date.parse(a.updated_at ?? '') || 0) ? b : a;
}

export function AlarmStatesProvider({ children }: { children: ReactNode }) {
  const { visible, homes, devices, loading: devLoading, failed } = useScope();
  const listedRefs = useMemo(() => listedSet(devices), [devices]);
  const failedApis = useMemo(() => new Set(failed.map((p) => p.api)), [failed]);
  const { subscribeAlarm, subscribe } = useLiveEvents();
  const [watched, setWatched] = useState<Record<string, number>>({});
  const watch = useCallback((homeId: string) => {
    setWatched((w) => ({ ...w, [homeId]: (w[homeId] ?? 0) + 1 }));
    return () =>
      setWatched((w) => {
        const n = { ...w };
        if ((n[homeId] ?? 0) <= 1) delete n[homeId];
        else n[homeId] -= 1;
        return n;
      });
  }, []);
  const extra = useMemo(() => homes.filter((h) => watched[h.home_id] && !visible.some((v) => v.home_id === h.home_id)), [homes, watched, visible]);
  const xs = useQueries({
    queries: extra.map((h) => ({
      queryKey: ['alarms', h.endpoints.api, h.home_id],
      queryFn: () => alarmApi.list(h.endpoints.api, h.home_id, { limit: 200 }),
      refetchInterval: 60_000,
      staleTime: 10_000,
      retry: false,
    })),
  });
  const qc = useQueryClient();
  const [overlay, setOverlay] = useState(new Map<string, AlarmInfo>());
  const qs = useQueries({
    queries: visible.map((h) => ({
      queryKey: ['alarms', h.endpoints.api, h.home_id],
      queryFn: () => alarmApi.list(h.endpoints.api, h.home_id, { limit: 200 }),
      refetchInterval: 60_000,
      staleTime: 10_000,
      retry: false,
    })),
  });
  // 還沒處理的完整清單（合併次數用；第六輪 B6-03）
  const openQs = useQueries({
    queries: visible.map((h) => ({
      queryKey: ['alarms', h.endpoints.api, h.home_id, 'open'],
      queryFn: () => listOpen(h.endpoints.api, h.home_id),
      refetchInterval: 60_000,
      staleTime: 10_000,
      retry: false,
    })),
  });
  const homeIdx = useMemo(() => new Map(visible.map((h, i) => [h.home_id, i])), [visible]);

  // 變更之後重撈那個家戶（未處理數）；同一個家戶 1 秒內合併
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const refetchSoon = useCallback(
    (homeId: string) => {
      if (timers.current.has(homeId)) return;
      timers.current.set(
        homeId,
        setTimeout(() => {
          timers.current.delete(homeId);
          void qc.invalidateQueries({ queryKey: ['alarms'], predicate: (q) => q.queryKey[2] === homeId });
        }, 1000),
      );
    },
    [qc],
  );
  useEffect(() => () => timers.current.forEach((t) => clearTimeout(t)), []);

  const set = useCallback((homeId: string, eventId: string, info: AlarmInfo) => {
    refetchSoon(homeId); // 未處理數跟著變
    setOverlay((m) => {
      const n = new Map(m);
      n.delete(eventId);
      n.set(eventId, info);
      while (n.size > OVERLAY_MAX) n.delete(n.keys().next().value as string);
      return n;
    });
  }, [refetchSoon]);

  // 警報狀態幀（EVT-06 / EVT-07）：指派、只加備註、逾時的幀也是 alarm_state，但 web-sdk 只解析狀態欄位（沒有 kind、被指派者），
  // 從幀推的「誰在處理」可能是錯的（加備註的人不是接手的人）→ 一律重讀那一則，以 API 為準；讀不到才用幀（只有真的換了狀態時）。
  const apiOfRef = useRef<(homeId: string) => string | undefined>(() => undefined);
  apiOfRef.current = (homeId) => (visible.find((h) => h.home_id === homeId) ?? homes.find((h) => h.home_id === homeId))?.endpoints.api;
  useEffect(
    () =>
      subscribeAlarm((f) => {
        const api = apiOfRef.current(f.homeId);
        const fallback = () => {
          if (f.from !== f.state) set(f.homeId, f.eventId, infoFromFrame(f));
          else refetchSoon(f.homeId);
        };
        if (!api) return fallback();
        alarmApi
          .get(api, f.homeId, f.eventId)
          .then((r) => (r?.alarm?.state ? set(f.homeId, f.eventId, r.alarm) : fallback()))
          .catch(fallback);
      }),
    [subscribeAlarm, set, refetchSoon],
  );
  // 新的警報事件進來：未處理數要加
  useEffect(
    () =>
      subscribe((e) => {
        if ((e.event as { level?: string }).level === 'alarm') refetchSoon(e.homeId);
      }),
    [subscribe, refetchSoon],
  );

  const slas = useMemo(() => {
    const m = new Map<string, AlarmSla>();
    for (const q of [...openQs.map((o) => ({ data: o.data ? { alarms: o.data.items } : undefined })), ...qs, ...xs])
      for (const a of q.data?.alarms ?? []) if (a.sla) m.set(a.event_id, a.sla);
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qs.map((q) => q.dataUpdatedAt).join(), xs.map((q) => q.dataUpdatedAt).join(), openQs.map((q) => q.dataUpdatedAt).join()]);
  const fromLists = useMemo(() => {
    const m = new Map<string, AlarmInfo>();
    for (const q of openQs) for (const a of q.data?.items ?? []) m.set(a.event_id, a.alarm);
    for (const q of [...qs, ...xs]) for (const a of q.data?.alarms ?? []) m.set(a.event_id, newer(m.get(a.event_id), a.alarm)!);
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qs.map((q) => q.dataUpdatedAt).join(), xs.map((q) => q.dataUpdatedAt).join(), openQs.map((q) => q.dataUpdatedAt).join()]);
  // 還沒處理的：完整清單讀到了用它，還沒讀到（或讀不到）的家戶先用最近 200 則
  const items = useMemo(() => {
    const seen = new Set<string>();
    return qs
      .flatMap((q, i) => (openQs[i]?.isSuccess ? openQs[i].data.items : (q.data?.alarms ?? [])))
      .filter((a) => (seen.has(a.event_id) ? false : (seen.add(a.event_id), true)))
      .sort((a, b) => (Date.parse(b.occurred_at) || 0) - (Date.parse(a.occurred_at) || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qs.map((q) => q.dataUpdatedAt).join(), openQs.map((q) => q.dataUpdatedAt).join()]);

  const value = useMemo<Ctx>(() => {
    const ok = qs.filter((q) => q.isSuccess);
    const openList = openEpisodes(items.filter((a) => isUnhandled(newer(overlay.get(a.event_id), a.alarm)?.state)));
    const agg = aggregateOpen(openList);
    const openReady = openQs.every((q) => q.isSuccess || q.isError);
    // 未登記 / 已移除設備的警報不算進計數（SPEC #vms-web-alarm-tidy）：「我的設備」讀到了、那個 plane 沒失敗才判斷
    const listed = (homeId: string, ref: string) => {
      const h = visible.find((x) => x.home_id === homeId) ?? extra.find((x) => x.home_id === homeId);
      if (devLoading || (h && failedApis.has(h.endpoints.api))) return true;
      return listedRefs.has(`${homeId}/${ref}`);
    };
    // 計數改用完整的未處理清單（一次一列）扣掉未登記的；還沒讀到 → null（「計算中」）；清單讀不到的家戶用平台的 unhandled
    const count = tidyCount(
      visible.map((h, i) => {
        const o = openQs[i];
        const l = qs[i];
        if (devLoading || o?.isPending) return { kind: 'pending' as const };
        if (o?.isSuccess) {
          const mine = openList.filter((a) => a.home_id === h.home_id);
          const nListed = mine.filter((a) => listed(a.home_id, a.device_ref)).length;
          return { kind: 'list' as const, listed: nListed, unlisted: mine.length - nListed, more: o.data.more, platform: l?.data?.unhandled };
        }
        if (l?.isSuccess) return { kind: 'platform' as const, n: l.data.unhandled };
        if (l?.isPending) return { kind: 'pending' as const };
        return { kind: 'none' as const };
      }),
    );
    const unlistedOpen = openReady && !devLoading ? openList.filter((a) => !listed(a.home_id, a.device_ref)).length : 0;
    return {
      infoOf: (id) => newer(overlay.get(id), fromLists.get(id)),
      canHandle: (homeId) => {
        const i = homeIdx.get(homeId);
        if (i !== undefined) return !!qs[i]?.data?.can_handle;
        const j = extra.findIndex((h) => h.home_id === homeId);
        return j >= 0 && !!xs[j]?.data?.can_handle;
      },
      apiOf: (homeId) => (visible.find((h) => h.home_id === homeId) ?? extra.find((h) => h.home_id === homeId))?.endpoints.api,
      unhandled: count.n,
      unhandledMore: count.more,
      listed,
      unlistedOpen,
      // 算好了就看扣掉未登記之後的數；還沒算好先看平台說有沒有（事件頁預設分頁、「計算中」那一行）
      hasUnhandled: count.n !== null ? count.n > 0 : ok.length ? ok.some((q) => (q.data?.unhandled ?? 0) > 0) : null,
      openList,
      openAgg: (homeId, deviceRef, key) => agg.get(aggKey(homeId, deviceRef, key)),
      openReady,
      slaOf: (id) => slas.get(id),
      set,
      watch,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay, fromLists, slas, items, homeIdx, visible, extra, set, watch, listedRefs, devLoading, failedApis, qs.map((q) => `${q.dataUpdatedAt}:${q.status}`).join(), openQs.map((q) => q.status).join(), xs.map((q) => `${q.dataUpdatedAt}:${q.status}`).join()]);
  return <AlarmCtx.Provider value={value}>{children}</AlarmCtx.Provider>;
}

export function useAlarmStates(): Ctx {
  return useContext(AlarmCtx);
}
